import mime from 'mime';
import z from 'zod';

import { isDefined } from '../../utils.js';
import { BaseItemSchema } from '../common.js';

/**
 * Knowledge base file should at least include all content types that
 * predict documents service supports for file upload to LLM models.
 */
export const VALID_CONTENT_TYPES = [
  'application/json',
  'application/pdf',
  'application/vnd.ms-excel',
  'application/vnd.ms-word',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/x-javascript',
  'application/x-python',
  'audio/aac',
  'audio/flac',
  'audio/m4a',
  'audio/mp4',
  'audio/mpeg',
  'audio/mpga',
  'audio/opus',
  'audio/pcm',
  'audio/wav',
  'audio/webm',
  'image/heic',
  'image/heif',
  'image/jpeg',
  'image/png',
  'image/tif',
  'image/tiff',
  'image/webp',
  'text/css',
  'text/csv',
  'text/html',
  'text/javascript',
  'text/md',
  'text/plain',
  'text/rtf',
  'text/x-python',
  'text/xml',
  'video/3gpp',
  'video/mpegps',
  'video/mpg',
  'video/quicktime',
  'video/wmv',
  'video/x-flv',
];

export const VALID_EXTENSIONS = new Set(
  VALID_CONTENT_TYPES.map((contentType) => mime.getExtension(contentType)).filter(isDefined)
);

export const MAX_KNOWLEDGE_BASE_FILES_PER_BLOCK = 10;
export const MAX_KNOWLEDGE_BASE_FILE_SIZE_IN_BYTES = 50 * 1024 * 1024; // 50MB

export const KnowledgeBaseFileSchema = z
  .object({
    name: z.string(),
    organizationId: z.uuid(),
    displayedType: z
      .string()
      .refine((val) => VALID_EXTENSIONS.has(val))
      .optional(),
    dataSizeBytes: z.number().optional(),
    uploadedById: z.uuid(),
    version: z.number(),
  })
  .extend(BaseItemSchema.shape);
export type KnowledgeBaseFile = z.infer<typeof KnowledgeBaseFileSchema>;

export const KnowledgeBaseFileFindAllOptionsSchema = KnowledgeBaseFileSchema.pick({
  organizationId: true,
  name: true,
  displayedType: true,
}).partial();
export type FindAllOptions = z.infer<typeof KnowledgeBaseFileFindAllOptionsSchema>;

export const KnowledgeBaseFileCreateSchema = KnowledgeBaseFileSchema.pick({
  name: true,
  organizationId: true,
  displayedType: true,
  dataSizeBytes: true,
}).required({
  name: true,
  organizationId: true,
  displayedType: true,
  dataSizeBytes: true,
});

export type CreateParams = z.infer<typeof KnowledgeBaseFileCreateSchema>;
